import { useState, useRef } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface IngestionDateRangePickerProps {
  value: { start: Date; end: Date };
  onChange: (start: Date, end: Date) => void;
  onJumpAroundTime?: (time: Date) => void;
  className?: string;
}

const PRESETS: { label: string; minutes: number }[] = [
  { label: '15m', minutes: 15 },
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
  { label: '7d', minutes: 10080 },
  { label: '30d', minutes: 43200 },
];

function formatRange(start: Date, end: Date): string {
  const diffMs = end.getTime() - start.getTime();
  const diffMins = diffMs / (1000 * 60);

  // Check if it matches a preset (within 30s tolerance)
  for (const preset of PRESETS) {
    if (Math.abs(diffMins - preset.minutes) < 0.5) {
      const now = new Date();
      const endDiff = Math.abs(now.getTime() - end.getTime());
      if (endDiff < 60_000) return `Last ${preset.label}`;
    }
  }

  const fmt = (d: Date) =>
    d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  return `${fmt(start)} — ${fmt(end)}`;
}

function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fromDatetimeLocal(s: string): Date | null {
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function IngestionDateRangePicker({
  value,
  onChange,
  onJumpAroundTime,
  className,
}: IngestionDateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [aroundTime, setAroundTime] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const aroundInputRef = useRef<HTMLInputElement>(null);

  const handlePreset = (minutes: number) => {
    const end = new Date();
    const start = new Date(end.getTime() - minutes * 60 * 1000);
    onChange(start, end);
    setIsOpen(false);
  };

  const handleAroundTime = () => {
    const time = fromDatetimeLocal(aroundTime);
    if (!time) return;
    if (onJumpAroundTime) {
      onJumpAroundTime(time);
    } else {
      // Fallback: set ±30min range
      const start = new Date(time.getTime() - 30 * 60 * 1000);
      const end = new Date(time.getTime() + 30 * 60 * 1000);
      onChange(start, end);
    }
    setIsOpen(false);
  };

  const handleCustomRange = () => {
    const start = fromDatetimeLocal(customStart);
    const end = fromDatetimeLocal(customEnd);
    if (!start || !end || start >= end) return;
    onChange(start, end);
    setIsOpen(false);
  };

  const handleOpen = (open: boolean) => {
    if (open) {
      setCustomStart(toDatetimeLocal(value.start));
      setCustomEnd(toDatetimeLocal(value.end));
    }
    setIsOpen(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'h-8 justify-between gap-2 border-[#2E3835] bg-[#1a1a1a] px-3 text-xs text-white hover:bg-[#2a2a2a]',
            className
          )}
        >
          <Clock className="h-3.5 w-3.5 text-white/50" />
          <span>{formatRange(value.start, value.end)}</span>
          <ChevronDown className="h-3 w-3 text-white/50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[320px] border-[#2E3835] bg-[#1a1a1a] p-0"
        align="end"
      >
        {/* Quick presets */}
        <div className="border-b border-[#2E3835] px-3 py-2.5">
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-white/30">
            Quick
          </div>
          <div className="flex gap-1">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => handlePreset(p.minutes)}
                className="flex-1 rounded-md border border-[#2E3835] px-2 py-1 text-xs text-white/70 transition-colors hover:border-white/20 hover:bg-white/5 hover:text-white"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Around a time */}
        <div className="border-b border-[#2E3835] px-3 py-2.5">
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-white/30">
            Around a time
          </div>
          <div className="flex gap-1.5">
            <input
              ref={aroundInputRef}
              type="datetime-local"
              step="1"
              value={aroundTime}
              onChange={(e) => setAroundTime(e.target.value)}
              className="flex-1 rounded-md border border-[#2E3835] bg-[#111] px-2 py-1 text-xs text-white/80 outline-none focus:border-[#D1F551]/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-40"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAroundTime}
              disabled={!aroundTime}
              className="h-7 border-[#2E3835] bg-transparent px-3 text-xs hover:bg-white/5"
            >
              Go
            </Button>
          </div>
        </div>

        {/* Custom range */}
        <div className="px-3 py-2.5">
          <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-white/30">
            Custom range
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="w-10 text-[10px] text-white/40">From</span>
              <input
                type="datetime-local"
                step="1"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="flex-1 rounded-md border border-[#2E3835] bg-[#111] px-2 py-1 text-xs text-white/80 outline-none focus:border-[#D1F551]/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="w-10 text-[10px] text-white/40">To</span>
              <input
                type="datetime-local"
                step="1"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="flex-1 rounded-md border border-[#2E3835] bg-[#111] px-2 py-1 text-xs text-white/80 outline-none focus:border-[#D1F551]/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-40"
              />
            </div>
            <div className="flex justify-end pt-0.5">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCustomRange}
                className="h-7 border-[#2E3835] bg-transparent px-4 text-xs hover:bg-white/5"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
